'use client';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useGlobalClientContext } from '../common/GlobalClientContext';
import WarningOutlineIcon from '../icons/Warning.outline';
import { I18nResKeys } from '@altus-ui/i18n/messages/i18n-types';
import { Button } from '@altus-ui/components_v2/v3/Button';
import { Input } from '@altus-ui/components_v2/v3/Input';
import { Select, SelectOption } from '@altus-ui/components_v2/v3/Select';
import { Checkbox } from '@altus-ui/components_v2/v3/Checkbox';
import ConsentDetailModal from '../consent/consentDetailModal';
import { ConsentContext } from '../consent/consentContext';
import { addMonths, formatDate } from 'date-fns';
import { interestSubmit } from '../../services/signupService';
import { toastFail } from '../components/Toast';
import InterestComplete from './interestComplete';
import { validatedEmail } from '../../utils/validator';
import { numberFormat } from '../../utils/number';
import InterestGpuClass from './interestGpuClass';
import { GpuClassType } from './type';
import { RequiredError } from './interestError';
import { ConsentType, ConsentTypes } from '../consent/type';
import { convertTaggedTextToNodes } from '@altus-ui/components_v2/utils/text';

interface Interest {
  email: string;
  businessName: string;
  primaryContactTitle: string;
  lastName: string;
  primaryContactPhone: string;
  earliestMonthYouNeedAccess: string;
  numberOfGpus: string;
  highPerformanceCompute: string;
  agreementPICollection: boolean;
  agreementPIMarketing?: boolean;
  agreementReceivePromos?: boolean;
  agreementDate: string;
  gpuClass: string;
}

const TextAreaClassName = [
  'placeholder:font-display',
  'placeholder:font-regular',
  'placeholder:text-sm',
  'placeholder:leading-md',
  'placeholder:text-tertiary',
  'h-[120px]',
  'px-l',
  'py-s',
  'font-body',
  'font-regular',
  'text-sm',
  'leading-md',
  'text-primary',
  'border-gray-100',
  'border-solid',
  'border',
  'rounded-sm',
  'w-full'
].join(' ');

const SectionClassName = 'flex flex-col items-start gap-l w-full';

const SectionTitleClassName =
  'font-body font-semibold text-xl leading-lg text-primary';

const FieldGroupClassName = 'flex flex-col gap-3xs w-full';

const FieldRowClassName =
  'flex flex-row items-start justify-between gap-s w-full';

const FieldLabelClassName =
  'font-body font-medium text-sm leading-2xs text-primary flex flex-row justify-start items-center gap-4xs';

const SectionDescriptionClassName =
  'font-body font-regular text-sm leading-md text-secondary';

const CONSENT_ROWS: Array<{
  consentType: ConsentType;
  dialogTitleKey: string;
  labelKey: string;
  isRequired: boolean;
}> = [
  {
    consentType: ConsentTypes.consent_personal_information_market,
    dialogTitleKey: 'consent.to.marketing_title',
    labelKey: 'consent.title.consent_personal_information_market',
    isRequired: false
  },
  {
    consentType: ConsentTypes.consent_receive_promotional,
    dialogTitleKey: 'consent.to.promotional_title',
    labelKey: 'consent.reset.password_4th_consent',
    isRequired: false
  }
];

const NOTICE_ROWS: Array<{
  consentType: ConsentType;
  dialogTitleKey: string;
  labelKey: string;
}> = [
  {
    consentType: ConsentTypes.consent_personal_information,
    dialogTitleKey: 'collection.and.use.of.personal.information_title',
    labelKey: 'consent.reset.password_2nd_consent'
  }
];

const ALL_CONSENT_ROWS = [...CONSENT_ROWS, ...NOTICE_ROWS];

const consentDialogTitleKeyMap = ALL_CONSENT_ROWS.reduce<
  Partial<Record<ConsentType, string>>
>((acc, { consentType, dialogTitleKey }) => {
  acc[consentType] = dialogTitleKey;
  return acc;
}, {});

const SubmitButtonClassName = ['w-full', 'font-body'].join(' ');

export default function InterestContent() {
  const {
    i18nHelper: { t }
  } = useGlobalClientContext();

  const [interest, setInterest] = useState<Interest>({
    email: '',
    businessName: '',
    primaryContactTitle: '',
    lastName: '',
    primaryContactPhone: '',
    earliestMonthYouNeedAccess: '',
    numberOfGpus: '',
    highPerformanceCompute: '',
    agreementPICollection: false,
    agreementPIMarketing: false,
    agreementReceivePromos: false,
    gpuClass: '',
    agreementDate: ''
  });

  const IGNORED_KEYS = new Set([
    'agreementPICollection',
    'agreementPIMarketing',
    'agreementReceivePromos',
    'agreementDate'
  ]);

  const [validating, setValidating] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [complete, setComplete] = useState<boolean>(false);

  const [gpuClassType, setGpuClassType] =
    useState<GpuClassType>('GPU_CLASS_SELECT');
  const [gpuClassSelect, setGpuClassSelect] = useState<string[]>([]);
  const [gpuClassInput, setGpuClassInput] = useState<string>('');

  const {
    consentDetailOpen,
    setConsentDetailOpen,
    selectedConsentType,
    detailContent,
    handleOpenDetailDialog
  } = useContext(ConsentContext);

  const handleChangeInfo = (key: string, value: any) => {
    setInterest((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleOpenDetailDialogWithDate = (consentType: ConsentType) => {
    const isNotice = NOTICE_ROWS.some((row) => row.consentType === consentType);
    if (isNotice) {
      if (!interest.agreementDate) {
        handleChangeInfo('agreementDate', formatDate(new Date(), 'yyyy-MM-dd'));
      }
    }
    handleOpenDetailDialog(consentType);
  };

  const missingInput = (key: string) => {
    return validating && !interest[key as keyof Interest];
  };

  const consentToFieldNameMap: Partial<Record<ConsentType, keyof Interest>> = {
    [ConsentTypes.consent_personal_information_market]: 'agreementPIMarketing',
    [ConsentTypes.consent_receive_promotional]: 'agreementReceivePromos'
  };

  const isInvalidated = (consentType: ConsentType) => {
    const consentRow = CONSENT_ROWS.find(
      (row) => row.consentType === consentType
    );
    if (!consentRow || !consentRow.isRequired) return false;

    const fieldName = consentToFieldNameMap[consentType];
    return !!fieldName && validating && !interest[fieldName];
  };

  const handleCheck = (consentType: ConsentType, checked: boolean) => {
    const fieldName = consentToFieldNameMap[consentType];
    if (!fieldName) return;

    handleChangeInfo(fieldName, checked);

    if (
      consentType === ConsentTypes.consent_personal_information_market &&
      !checked
    ) {
      handleChangeInfo('agreementReceivePromos', false);
    }

    if (consentType === ConsentTypes.consent_receive_promotional && checked) {
      handleChangeInfo('agreementPIMarketing', true);
    }
  };

  const wrongFormat = (value: any, validator: (value: any) => boolean) => {
    return validating && !validator(value);
  };

  const validatedPhone = (value: string) => {
    const phoneRegex = /^\+82-\d{2}-\d{4}-\d{4}$/;
    return phoneRegex.test(value);
  };

  const getUpComming6Months = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 6; i++) {
      months.push(getMonthOption(currentDate, i));
    }
    return months;
  };

  const getMonthOption = (date: Date, next: number) => {
    const targetDate = addMonths(date, next);
    targetDate.setDate(1);
    const sameYear = date.getFullYear() === targetDate.getFullYear();
    return {
      label: `${t(`month.${targetDate.getMonth() + 1}` as I18nResKeys, {})}${sameYear ? '' : `, ${targetDate.getFullYear()}`}`,
      value: formatDate(targetDate, 'yyyy-MM-dd')
    };
  };

  const numberOfGpusOptions = [
    { label: '1-8', value: '1-8' },
    { label: '9-32', value: '9-32' },
    { label: '33-64', value: '33-64' },
    { label: '65-128', value: '65-128' },
    { label: '128+', value: '128+' }
  ];

  const isFormValid = () => {
    return (
      Object.entries(interest)
        .filter(([key]) => !IGNORED_KEYS.has(key))
        .every(([, value]) => value) &&
      validatedEmail(interest.email) &&
      validatedPhone(interest.primaryContactPhone)
    );
  };

  const handleSubmit = async () => {
    setValidating(true);
    if (
      Object.entries(interest)
        .filter(([key]) => !IGNORED_KEYS.has(key))
        .some(([, value]) => !value) ||
      !validatedEmail(interest.email) ||
      !validatedPhone(interest.primaryContactPhone)
    ) {
      return;
    }
    setValidating(false);
    setSubmitting(true);
    const interestWithDate = {
      ...interest,
      agreementDate:
        interest.agreementDate || formatDate(new Date(), 'yyyy-MM-dd')
    };
    const response = await interestSubmit(interestWithDate);
    if (!response.success) {
      toastFail(t('interest.error.submit', {}));
    } else {
      setComplete(true);
    }
    setSubmitting(false);
  };

  const textareaClassNames = useCallback(
    (key: string) => {
      return [
        TextAreaClassName,
        missingInput(key) ? 'border-red-500' : '',
        submitting ? '!bg-gray-75 !border-gray-200 !text-gray-400' : ''
      ].join(' ');
    },
    [interest, validating, submitting]
  );

  useEffect(() => {
    if (gpuClassType === 'GPU_CLASS_SELECT') {
      setInterest((prev: any) => ({
        ...prev,
        gpuClass: gpuClassSelect.join(',')
      }));
    } else {
      setInterest((prev: any) => ({ ...prev, gpuClass: gpuClassInput }));
    }
  }, [gpuClassType, gpuClassSelect, gpuClassInput]);

  return complete ? (
    <InterestComplete />
  ) : (
    <div className="w-[560px] h-fit rounded-m bg-white border border-gray-100 border-solid shadow-xl p-7xl flex flex-col gap-5xl">
      <div className="flex flex-col justify-start items-start gap-2xs w-full">
        <span className="font-display font-semibold text-3xl leading-4xl text-primary">
          {t('talk_to_expert.capitalize', {})}
        </span>
        <span className="font-body font-regular text-md leading-lg text-secondary">
          {t('interest.describe', {})}
        </span>
      </div>
      <div className={SectionClassName}>
        <div className={SectionTitleClassName}>
          {t('interest.general_info', {})}
        </div>
        <div className="flex flex-col gap-l items-start w-full">
          {/** email */}
          <div className={FieldGroupClassName}>
            <div className={FieldLabelClassName}>
              <span>{t('email.capitalize', {})}</span>
            </div>
            <Input
              value={interest.email}
              onValueChange={(value: string) =>
                handleChangeInfo('email', value)
              }
              placeholder={t('interest.email.placeholder', {})}
              disabled={submitting}
              error={
                missingInput('email') ||
                wrongFormat(interest.email, validatedEmail)
              }
              showLabel={false}
              showHintText={false}
            />
            {missingInput('email') && <RequiredError />}
            {validating &&
              !missingInput('email') &&
              !validatedEmail(interest.email) && (
                <div className="flex flex-row justify-start items-start gap-3xs">
                  <WarningOutlineIcon />
                  <span className="text-red-500 leading-xs font-regular text-xs">
                    {t('interest.email.validate.error', {})}
                  </span>
                </div>
              )}
          </div>
          {/** fullname + phone number */}
          <div className={FieldRowClassName}>
            <div className={`${FieldGroupClassName} flex-1`}>
              <div className={FieldLabelClassName}>
                <span>{t('interest.fullname', {})}</span>
              </div>
              <Input
                value={interest.lastName}
                onValueChange={(value: string) =>
                  handleChangeInfo('lastName', value)
                }
                placeholder={t('interest.fullname.placeholder', {})}
                disabled={submitting}
                error={missingInput('lastName')}
                showLabel={false}
                showHintText={false}
              />
              {missingInput('lastName') && <RequiredError />}
            </div>
            <div className={`${FieldGroupClassName} flex-1`}>
              <div className={FieldLabelClassName}>
                <span>{t('interest.phone', {})}</span>
              </div>
              <Input
                value={interest.primaryContactPhone}
                onValueChange={(value: string) =>
                  handleChangeInfo(
                    'primaryContactPhone',
                    numberFormat('##-####-####', '-', '+82-')(value)
                  )
                }
                placeholder={t('interest.phone.placeholder', {})}
                disabled={submitting}
                error={
                  missingInput('primaryContactPhone') ||
                  wrongFormat(interest.primaryContactPhone, validatedPhone)
                }
                showLabel={false}
                showHintText={false}
              />
              {missingInput('primaryContactPhone') && <RequiredError />}
              {validating &&
                !missingInput('primaryContactPhone') &&
                !validatedPhone(interest.primaryContactPhone) && (
                  <div className="flex flex-row justify-start items-start gap-3xs">
                    <WarningOutlineIcon />
                    <span className="text-red-500 leading-xs font-regular text-xs">
                      {t('interest.phone.validate.error', {})}
                    </span>
                  </div>
                )}
            </div>
          </div>
          {/** business name + position/title */}
          <div className={FieldRowClassName}>
            <div className={`${FieldGroupClassName} flex-1`}>
              <div className={FieldLabelClassName}>
                <span>{t('interest.business_name', {})}</span>
              </div>
              <Input
                value={interest.businessName}
                onValueChange={(value: string) =>
                  handleChangeInfo('businessName', value)
                }
                placeholder={t('interest.business_name.placeholder', {})}
                disabled={submitting}
                error={missingInput('businessName')}
                showLabel={false}
                showHintText={false}
              />
              {missingInput('businessName') && <RequiredError />}
            </div>
            <div className={`${FieldGroupClassName} flex-1`}>
              <div className={FieldLabelClassName}>
                <span>{t('interest.primary_contact_title', {})}</span>
              </div>
              <Input
                value={interest.primaryContactTitle}
                onValueChange={(value: string) =>
                  handleChangeInfo('primaryContactTitle', value)
                }
                placeholder={t(
                  'interest.primary_contact_title.placeholder',
                  {}
                )}
                disabled={submitting}
                error={missingInput('primaryContactTitle')}
                showLabel={false}
                showHintText={false}
              />
              {missingInput('primaryContactTitle') && <RequiredError />}
            </div>
          </div>
          {/** earlist month */}
          <div className={FieldGroupClassName}>
            <div className={FieldLabelClassName}>
              <span>{t('interest.earliestMonthYouNeedAccess', {})}</span>
            </div>
            <Select
              value={interest.earliestMonthYouNeedAccess}
              placeholder={t(
                'interest.earliestMonthYouNeedAccess.placeholder',
                {}
              )}
              onChange={(value) =>
                handleChangeInfo('earliestMonthYouNeedAccess', value)
              }
              disabled={submitting}
              className="w-full">
              {getUpComming6Months().map((item) => (
                <SelectOption key={item.value} value={item.value}>
                  {item.label}
                </SelectOption>
              ))}
            </Select>
            {missingInput('earliestMonthYouNeedAccess') && <RequiredError />}
          </div>
        </div>
      </div>
      {/** Gpu Class */}
      <div className={SectionClassName}>
        <div className={SectionTitleClassName}>
          {t('interest.gpu_preference', {})}
        </div>
        <div className="flex flex-col gap-2xl w-full">
          <div className={`${FieldGroupClassName} flex-1`}>
            <div className={FieldLabelClassName}>
              <span>{t('interest.numberOfGpus', {})}</span>
            </div>
            <Select
              value={interest.numberOfGpus}
              placeholder={t('interest.numberOfGpus.placeholder', {})}
              onChange={(value) => handleChangeInfo('numberOfGpus', value)}
              disabled={submitting}
              className="w-full">
              {numberOfGpusOptions.map((item) => (
                <SelectOption key={item.value} value={item.value}>
                  {item.label}
                </SelectOption>
              ))}
            </Select>
            {missingInput('numberOfGpus') && <RequiredError />}
          </div>
          <InterestGpuClass
            validating={validating}
            disabled={submitting}
            gpuClassType={gpuClassType}
            setGpuClassType={setGpuClassType}
            gpuClassSelect={gpuClassSelect}
            setGpuClassSelect={setGpuClassSelect}
            gpuClassInput={gpuClassInput}
            setGpuClassInput={setGpuClassInput}
          />
        </div>
      </div>
      {/** Compute Needs */}
      <div className={SectionClassName}>
        <div className="flex flex-col gap-4xs w-full">
          <div className={SectionTitleClassName}>
            {t('interest.gpu_usage.detail', {})}
          </div>
          <div className={SectionDescriptionClassName}>
            <span>{t('interest.highPerformanceCompute', {})}</span>
          </div>
        </div>
        <div className="flex flex-col gap-3xs w-full">
          <textarea
            autoComplete="highPerformanceCompute"
            value={interest.highPerformanceCompute}
            name="highPerformanceCompute"
            onChange={(e) =>
              handleChangeInfo('highPerformanceCompute', e.target.value)
            }
            className={textareaClassNames('highPerformanceCompute')}
            placeholder={t('interest.highPerformanceCompute.placeholder', {})}
            maxLength={255}
            disabled={submitting}
          />
          {missingInput('highPerformanceCompute') && <RequiredError />}
        </div>
      </div>
      <div className={SectionClassName}>
        <div className={SectionTitleClassName}>
          {t('public.site.interest.form.agreement', {})}
        </div>
        <div className="flex flex-col justify-start items-start gap-2xs p-2xl rounded-sm border-solid border-[1px] border-tertiary bg-secondary">
          {CONSENT_ROWS.map(({ consentType, labelKey }) => (
            <div
              className="flex items-start flex-row gap-2xs"
              key={consentType}>
              <div className="h-[22px] flex items-center shrink-0">
                <Checkbox
                  onCheckedChange={(value) => {
                    handleCheck(consentType, value as boolean);
                  }}
                  checked={
                    !!interest[
                      consentToFieldNameMap[consentType] as keyof Interest
                    ]
                  }
                  id={consentType}
                  state={isInvalidated(consentType) ? 'destructive' : 'default'}
                  disabled={submitting}
                />
              </div>
              <div
                className={`font-regular text-sm leading-m relative hover:cursor-pointer ${isInvalidated(consentType) ? 'text-error-500' : 'text-primary'}`}
                onClick={() => {
                  if (submitting) return;
                  handleCheck(
                    consentType,
                    !interest[
                      consentToFieldNameMap[consentType] as keyof Interest
                    ]
                  );
                }}>
                {convertTaggedTextToNodes(t(labelKey as I18nResKeys, {}), {
                  renderLink: (value, key) => (
                    <span
                      key={key}
                      className={`font-body font-semibold text-sm leading-m ${isInvalidated(consentType) ? 'text-error-500' : 'text-brand'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetailDialogWithDate(consentType);
                      }}>
                      {value}
                    </span>
                  )
                })}
              </div>
            </div>
          ))}
          {NOTICE_ROWS.map(({ consentType, labelKey }) => (
            <div
              className="flex items-start flex-row gap-2xs pl-3xl"
              key={consentType}>
              <div className="font-regular text-sm leading-m text-primary">
                {convertTaggedTextToNodes(t(labelKey as I18nResKeys, {}), {
                  renderLink: (value, key) => (
                    <span
                      key={key}
                      className="font-body font-semibold text-sm leading-m text-brand cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetailDialogWithDate(consentType);
                      }}>
                      {value}
                    </span>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant={'primary'}
        size={'L'}
        onClick={handleSubmit}
        disabled={submitting || !isFormValid()}
        loading={submitting}
        className={SubmitButtonClassName}>
        {t('interest.submit', {})}
      </Button>

      {consentDetailOpen && (
        <ConsentDetailModal
          title={t(
            (consentDialogTitleKeyMap[selectedConsentType] ||
              `consent.title.${selectedConsentType}`) as I18nResKeys,
            {}
          )}
          children={detailContent}
          consentType={selectedConsentType}
          isNoticeConsent={NOTICE_ROWS.some(
            (row) => row.consentType === selectedConsentType
          )}
          onSubmit={(): void => {
            if (selectedConsentType === 'consent_personal_information_market') {
              handleChangeInfo('agreementPIMarketing', true);
            } else if (selectedConsentType === 'consent_receive_promotional') {
              handleChangeInfo('agreementReceivePromos', true);
              handleChangeInfo('agreementPIMarketing', true);
            }
            setConsentDetailOpen(false);
          }}
          onCancel={() => {
            setConsentDetailOpen(false);
          }}
        />
      )}
    </div>
  );
}
